import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackEvent } from '../analytics/posthog';
import { useAuth } from '../auth/use-auth';
import { DashboardPage } from '../dashboard/dashboard-page';
import { useRooms } from '../dashboard/use-rooms';
import { useHomeStore } from '../store/home-store';
import { FirstRun } from './first-run';
import { browsePathFor, linkedTarget } from './linked-target';

/**
 * "/" is two screens depending on whether you've been here before.
 *
 * With rooms, it's the dashboard: someone returning came back to get to
 * one, and a wall of screenshots is the fastest route there. With none,
 * it's the first-run canvas — there is nothing to show yet, so the screen
 * asks for the one thing it needs instead of rendering an empty grid.
 *
 * Rendered rather than redirected so the URL stays "/" either way: a bookmark
 * shouldn't break the day you capture your first site.
 */
export function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const target = linkedTarget(searchParams);
  const started = useRef(false);
  const { loading, rooms } = useRooms();
  const { user } = useAuth();
  const uid = user?.uid;
  const markWelcomed = useHomeStore((state) => state.markWelcomed);
  /**
   * Whether this user had already seen the welcome when home first knew who
   * they were. Read once per user — marking them welcomed below must not
   * swap the welcome out from under them mid-visit.
   */
  const welcomedBefore = useMemo(
    () => !!uid && useHomeStore.getState().welcomed.includes(uid),
    [uid],
  );
  const showingWelcome =
    !target && !loading && rooms.length === 0 && !welcomedBefore;

  // Seeing the welcome once is what turns it into "No Rooms Yet" next time.
  useEffect(() => {
    if (showingWelcome && uid) markWelcomed(uid);
  }, [showingWelcome, uid, markWelcomed]);

  /**
   * Sent here from floto.ai with a product to test (`/?url=…`): start the
   * session straight away — the click on the website was the decision. A
   * signed-out visitor reaches this only after sign-in and onboarding, which
   * carry the whole "/?url=…" through as their redirect.
   */
  useEffect(() => {
    if (!target || started.current) return;
    started.current = true;
    trackEvent('session_started', { source: 'website' });
    void navigate(browsePathFor(target), { replace: true });
  }, [target, navigate]);

  if (target) return <main className="min-h-svh bg-warm" />;

  // Nothing is shown while the answer is unknown. Flashing the first-run screen
  // at a returning user, then swapping it for their dashboard a moment later,
  // is worse than a beat of nothing.
  if (loading || !uid) {
    return <main className="min-h-svh bg-warm" />;
  }

  // No rooms: the welcome the first time, the dashboard's "No Rooms Yet"
  // after that (decided once, so the welcome doesn't vanish mid-visit).
  if (rooms.length > 0 || welcomedBefore) return <DashboardPage />;

  return <FirstRun />;
}
